import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { StatusBadge } from "../../components/admin/StatusBadge";
import { ConfirmDialog } from "../../components/admin/ConfirmDialog";
import { AdminMatchCandidates } from "../../components/admin/AdminMatchCandidates";
import { Button } from "../../components/ui/button";
import { formatDate, formatDateTime, formatTime } from "../../lib/dateUtils";

export default function AdminSessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmCancelApp, setConfirmCancelApp] = useState(null);
  const [confirmUndoMatch, setConfirmUndoMatch] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => {
    api.get(`/admin/sessions/${id}`).then(({ data }) => setData(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancelSession = async () => {
    try {
      await api.put(`/admin/sessions/${id}/cancel`);
      toast.success("Session cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel session");
    }
  };

  const cancelApplication = async (bookingId) => {
    try {
      await api.put(`/admin/applications/${bookingId}/cancel`);
      toast.success("Application cancelled");
      setSelected((s) => s.filter((x) => x !== bookingId));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel application");
    }
  };

  const toggleSelect = (bookingId) => {
    setSelected((s) => (s.includes(bookingId) ? s.filter((x) => x !== bookingId) : s.length < 2 ? [...s, bookingId] : s));
  };

  const createMatch = async (bookingIdA, bookingIdB) => {
    await api.post("/admin/applications/match", { session_id: id, booking_id_a: bookingIdA, booking_id_b: bookingIdB });
    toast.success("Match created!");
    setSelected([]);
    load();
  };

  const createSelectedMatch = async () => {
    if (selected.length !== 2) return toast.error("Select exactly two Strangers to match.");
    try {
      await createMatch(selected[0], selected[1]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const undoMatch = async (matchId) => {
    try {
      await api.put(`/admin/matches/${matchId}/undo`);
      toast.success("Match undone — both Strangers are back to Registered");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't undo match");
    }
  };

  if (!data) return <p className="font-medium text-midnight-black/60" data-testid="admin-session-detail-loading">Loading...</p>;
  const { session, photographer, registrations, matches, activity_logs } = data;
  const matchedBookingIds = new Set((matches || []).flatMap((m) => m.booking_ids));

  return (
    <div data-testid="admin-session-detail-page" className="max-w-4xl">
      <button onClick={() => navigate("/admin/sessions")} data-testid="back-to-sessions" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Sessions
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-extrabold uppercase text-2xl">{session.title}</h2>
          <p className="text-sm font-medium text-midnight-black/60">{session.city}, {session.state} · {formatDate(session.session_date)} · {formatTime(session.session_time)}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={session.status} testId="admin-session-detail-status" />
          {session.status !== "cancelled" && (
            <>
              <Button variant="outline" data-testid="admin-session-edit-button" className="rounded-full font-bold text-xs" onClick={() => navigate(`/admin/sessions/${id}/edit`)}>
                EDIT
              </Button>
              <Button variant="outline" data-testid="admin-session-cancel-button" className="rounded-full font-bold text-xs" onClick={() => setConfirmCancel(true)}>
                CANCEL SESSION
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
        <div>
          <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Session Details</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Type</dt><dd>{session.session_type}</dd></div>
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Price</dt><dd>${session.price}</dd></div>
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Capacity</dt><dd>{registrations.filter((r) => r.status !== "cancelled").length}/{session.capacity}</dd></div>
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Created</dt><dd>{formatDate(session.created_at)}</dd></div>
          </dl>
        </div>
        <div>
          <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Host Photographer</h3>
          {photographer.profile_id ? (
            <Link to={`/admin/photographers/${photographer.profile_id}`} data-testid="link-to-photographer-profile" className="inline-flex items-center gap-2 text-sm font-bold text-stranger-orange hover:underline">
              {photographer.name} — {photographer.business_name} <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <p className="text-sm text-midnight-black/60">{photographer.name} ({photographer.email})</p>
          )}
        </div>
      </div>

      {session.status !== "cancelled" && <AdminMatchCandidates sessionId={id} onCreateMatch={createMatch} />}

      <div className="mb-10">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="font-extrabold uppercase text-xs tracking-wide text-midnight-black/50">Applications ({registrations.length})</h3>
          {session.status !== "cancelled" && (
            <Button data-testid="admin-create-match-button" onClick={createSelectedMatch} disabled={selected.length !== 2} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full text-xs">
              MATCH SELECTED ({selected.length}/2)
            </Button>
          )}
        </div>
        {registrations.length === 0 ? (
          <p className="text-sm text-midnight-black/60" data-testid="no-session-registrations">No registrations yet.</p>
        ) : (
          <div className="space-y-2" data-testid="session-registrations-list">
            {registrations.map((r) => {
              const canSelect = r.status === "registered" && !matchedBookingIds.has(r.booking_id);
              return (
                <div
                  key={r.booking_id}
                  data-testid={`admin-application-row-${r.booking_id}`}
                  onClick={() => canSelect && toggleSelect(r.booking_id)}
                  className={`border-2 rounded-xl p-4 flex justify-between items-center text-sm font-medium gap-3 flex-wrap ${selected.includes(r.booking_id) ? "border-stranger-orange bg-warm-white" : "border-soft-gray"} ${canSelect ? "cursor-pointer" : ""}`}
                >
                  <span>{r.stranger_name} · {r.stranger_email}</span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} testId={`admin-application-status-${r.booking_id}`} />
                    {r.status !== "cancelled" && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`admin-cancel-application-${r.booking_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmCancelApp(r.booking_id);
                        }}
                        className="rounded-full font-bold text-[10px]"
                      >
                        CANCEL
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mb-10">
        <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Matches ({(matches || []).length})</h3>
        {!matches || matches.length === 0 ? (
          <p className="text-sm text-midnight-black/60" data-testid="no-session-matches">No matches created yet.</p>
        ) : (
          <div className="space-y-2" data-testid="session-matches-list">
            {matches.map((m) => (
              <div key={m.id} className="border border-soft-gray rounded-xl p-4" data-testid={`session-match-row-${m.id}`}>
                <div className="flex justify-between items-center text-sm font-medium">
                  <span>
                    {m.booking_ids
                      .map((bid) => registrations.find((r) => r.booking_id === bid)?.stranger_name)
                      .filter(Boolean)
                      .join(" + ")}
                    {m.match_score != null && (
                      <span className="ml-2 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-stranger-orange text-clean-white" data-testid={`session-match-score-${m.id}`}>
                        {m.match_score}% match
                      </span>
                    )}
                  </span>
                  <Button variant="outline" size="sm" data-testid={`admin-undo-match-${m.id}`} onClick={() => setConfirmUndoMatch(m.id)} className="rounded-full font-bold text-[10px]">
                    UNDO MATCH
                  </Button>
                </div>
                {m.match_reasons?.length > 0 ? (
                  <ul className="text-xs font-medium text-midnight-black/50 mt-2 list-disc list-inside">
                    {m.match_reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                ) : m.match_score == null ? (
                  <p className="text-xs text-midnight-black/40 mt-2">Score not recorded — this match was created before scoring was tracked.</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Audit Trail (This Session)</h3>
        {activity_logs.length === 0 ? (
          <p className="text-sm text-midnight-black/50" data-testid="no-session-activity-logs">No recorded admin activity for this session.</p>
        ) : (
          <div className="space-y-2" data-testid="session-activity-logs">
            {activity_logs.map((log) => (
              <div key={log.id} className="text-sm font-medium border-b border-soft-gray pb-2 flex justify-between">
                <span>{log.admin_name} — {log.action.replace(/_/g, " ")}</span>
                <span className="text-midnight-black/40 text-xs">{formatDateTime(log.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel this session?"
        description="All active registrations for this session will also be cancelled. This action is logged."
        confirmLabel="CANCEL SESSION"
        destructive
        onConfirm={cancelSession}
      />
      <ConfirmDialog
        open={!!confirmCancelApp}
        onOpenChange={(v) => !v && setConfirmCancelApp(null)}
        title="Cancel this application?"
        description="If this Stranger was matched, their match partner will be released back to Registered."
        confirmLabel="CANCEL APPLICATION"
        destructive
        onConfirm={() => cancelApplication(confirmCancelApp)}
      />
      <ConfirmDialog
        open={!!confirmUndoMatch}
        onOpenChange={(v) => !v && setConfirmUndoMatch(null)}
        title="Undo this match?"
        description="Both Strangers will be released back to Registered and can be matched again."
        confirmLabel="UNDO MATCH"
        destructive
        onConfirm={() => undoMatch(confirmUndoMatch)}
      />
    </div>
  );
}
